import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import versionControl from '../../../version-control.json';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

type TabType = 'geral' | 'frete' | 'colecoes' | 'lojista' | 'conta';

export const SupportPage: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('geral');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (
      tabParam === 'frete' ||
      tabParam === 'geral' ||
      tabParam === 'colecoes' ||
      tabParam === 'recursos' ||
      tabParam === 'lojista' ||
      tabParam === 'conta'
    ) {
      const targetTab: TabType = tabParam === 'recursos' ? 'colecoes' : (tabParam as TabType);
      setActiveTab(targetTab);
      if (tabParam === 'frete') {
        setOpenIndex(0); // auto-expand first question of freight tab
      }
    }
  }, [location]);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'geral', label: 'Dúvidas Gerais', icon: 'fa-circle-info' },
    { id: 'frete', label: 'Regras de Frete & Envios', icon: 'fa-truck-fast' },
    { id: 'colecoes', label: 'Coleções & Decks', icon: 'fa-layer-group' },
    { id: 'lojista', label: 'Lojistas & Vendas', icon: 'fa-store' },
    { id: 'conta', label: 'Conta & Segurança', icon: 'fa-user-shield' }
  ];

  const faqs: Record<TabType, FAQItem[]> = {
    geral: [
      {
        question: "O que é o Cardumy TCG e como a plataforma funciona?",
        answer: (
          <div className="space-y-2.5 text-slate-300">
            <p>
              O <strong className="text-white">Cardumy</strong> é a plataforma definitiva dedicada a jogadores e colecionadores de Trading Card Games (TCGs). Nosso ecossistema conecta fãs a lojistas parceiros verificados em todo o Brasil.
            </p>
            <p>Aqui você pode:</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-300">
              <li>Consultar cotações de mercado atualizadas em catálogo unificado multi-TCG.</li>
              <li>Montar decks competitivos e casuais com validação em tempo real de formatos oficiais.</li>
              <li>Organizar pastas de coleção pessoal, listas de desejos (Wantlist) e cartas para troca.</li>
              <li>Comprar cartas avulsas (singles), produtos selados e acessórios com carrinho unificado e checkout protegido.</li>
              <li>Descobrir e se inscrever em torneios locais e regionais com submissão direta de decklist.</li>
            </ul>
          </div>
        )
      },
      {
        question: "Quais jogos de cartas (TCGs) são suportados na plataforma?",
        answer: (
          <div className="space-y-2 text-slate-300">
            <p>O Cardumy oferece suporte a um dos maiores catálogos de TCGs do mercado, abrangendo:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1 text-xs">
              <span className="text-slate-300">• One Piece Card Game</span>
              <span className="text-slate-300">• Digimon TCG</span>
              <span className="text-slate-300">• Pokémon TCG</span>
              <span className="text-slate-300">• Magic: The Gathering</span>
              <span className="text-slate-300">• Yu-Gi-Oh! Official Card Game</span>
              <span className="text-slate-300">• Dragon Ball Super (Fusion & Masters)</span>
              <span className="text-slate-300">• Gundam Card Game</span>
              <span className="text-slate-300">• Disney Lorcana</span>
              <span className="text-slate-300">• Star Wars: Unlimited</span>
              <span className="text-slate-300">• Flesh and Blood</span>
              <span className="text-slate-300">• Cardfight!! Vanguard</span>
              <span className="text-slate-300">• Grand Archive TCG</span>
              <span className="text-slate-300">• Sorcery: Contested Realm</span>
              <span className="text-slate-300">• UniVersus & Altered TCG</span>
            </div>
            <p className="pt-2 text-slate-400 text-[11px]">
              Novas expansões, coleções e coleções de suporte são inseridas continuamente de acordo com o calendário oficial de lançamentos.
            </p>
          </div>
        )
      },
      {
        question: "O Cardumy é gratuito para jogadores e colecionadores?",
        answer: "Sim! Criar uma conta, gerenciar pastas ilimitadas de coleção, cadastrar cartas desejadas para troca, pesquisar valores no catálogo e utilizar todas as funções do Deckbuilder com regras oficiais é 100% gratuito para os usuários."
      },
      {
        question: "Como funciona a garantia de entrega e proteção nas compras?",
        answer: (
          <div className="space-y-2 text-slate-300">
            <p>
              Todas as transações realizadas no checkout oficial contam com a <strong className="text-white">Proteção ao Comprador Cardumy</strong>.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>O valor pago pelo comprador fica retido em custódia segura até que o lojista comprove a postagem e entrega do pedido.</li>
              <li>O repasse ao lojista ocorre após a confirmação de recebimento ou término do prazo de conferência de 7 dias úteis após a entrega.</li>
              <li>Caso o item não corresponda ao anunciado ou chegue danificado, nossa equipe intervém prontamente para mediação de reenvio ou reembolso.</li>
            </ul>
          </div>
        )
      },
      {
        question: "Como indicar ou solicitar a adição de uma nova loja parceira?",
        answer: "Se você conhece uma loja de TCG da sua cidade ou é proprietário de uma loja que ainda não está no Cardumy, você pode indicar através do nosso e-mail oficial (cardumyapp@gmail.com) informando o nome da loja, cidade/estado e Instagram ou site. Nossa equipe entrará em contato para agilizar o credenciamento."
      },
      {
        question: "Como participar dos Torneios cadastrados e acompanhar o Metagame?",
        answer: "Acesse a seção 'Torneios' no menu principal para encontrar eventos organizados por lojas parceiras. Ao confirmar sua vaga, você pode vincular a decklist criada no nosso Deckbuilder. Na seção 'Meta', você acompanha arquétipos em alta, taxa de vitórias e líderes dominantes dos principais torneios."
      }
    ],
    frete: [
      {
        question: "Modalidade: Carta Registrada / Envelopes com Seguro (Cartas Avulsas)",
        answer: (
          <div className="space-y-3 text-slate-300">
            <p>Se você está adquirindo apenas cartas avulsas (singles), este método é o mais econômico e tradicional do TCG nacional:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Peso Máximo Permitido:</strong> Até 500g (aproximadamente 200 a 250 cartas embaladas com segurança).</li>
              <li><strong className="text-white">Dimensão Máxima do Envelope:</strong> Comprimento de até 35 cm e largura de até 25 cm.</li>
              <li><strong className="text-white">Espessura Máxima:</strong> Até 2 cm (caso ultrapasse essa espessura, os Correios exigem o envio via Mini Envios ou PAC).</li>
              <li><strong className="text-white">Padrão de Embalagem Recomendado:</strong> Cartas protegidas em sleeves (shields) internos, acondicionadas em toploaders (plásticos rígidos) e lacradas com reforço de papelão firme para prevenir qualquer dobra ou umidade durante o transporte.</li>
            </ul>
          </div>
        )
      },
      {
        question: "Modalidade: Correios Mini Envios (Melhor Envio)",
        answer: (
          <div className="space-y-3 text-slate-300">
            <p>Uma excelente opção intermediária para remessas de pequeno porte, contendo sleeves, deckboxes compactas ou alguns boosters avulsos:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Peso Máximo Permitido:</strong> Até 300g / 350g.</li>
              <li><strong className="text-white">Dimensões Limites:</strong> Máximo de 24 cm (comprimento) x 16 cm (largura) x 4 cm (altura ou espessura).</li>
              <li><strong className="text-white">Rastreamento:</strong> Notificações de postagem, saída para entrega e registro de confirmação com assinatura.</li>
            </ul>
          </div>
        )
      },
      {
        question: "Modalidade: PAC, SEDEX & Transportadoras Privadas (Jadlog, Latam Cargo, Loggi)",
        answer: (
          <div className="space-y-3 text-slate-300">
            <p>Modalidade indicada para grandes volumes, acessórios rígidos (playmats com tubo, pastas grandes) ou produtos lacrados (booster boxes e bundles):</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Peso Máximo Permitido:</strong> Até 30 kg para entregas nacionais.</li>
              <li><strong className="text-white">Dimensões Máximas da Caixa:</strong> Nenhuma dimensão individual pode ultrapassar 100 cm.</li>
              <li><strong className="text-white">Soma das Dimensões (C + L + A):</strong> A soma das três dimensões não deve ultrapassar 200 cm.</li>
              <li><strong className="text-white">Embalagem Exigida:</strong> Caixas de papelão reforçadas com amortecimento interno de plástico bolha ou almofadas de ar para evitar amassados em caixas seladas.</li>
            </ul>
          </div>
        )
      },
      {
        question: "Como é calculado o valor do frete e o prazo estimado?",
        answer: "O frete é calculado de forma dinâmica via integração direta com as cotações do Melhor Envio e Correios. O cálculo leva em conta o CEP de origem do lojista parceiro, o seu CEP de destino, o peso e a cubagem calculada da embalagem. É possível comparar opções econômicas e expressas antes de finalizar o pedido."
      },
      {
        question: "O que fazer em caso de atraso na entrega ou avaria na embalagem?",
        answer: (
          <div className="space-y-2 text-slate-300">
            <p>
              Caso o prazo informado pelos Correios ou transportadora expire sem atualização ou a encomenda chegue danificada:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Fotografe imediatamente a embalagem antes e durante a abertura, registrando o estado das cartas.</li>
              <li>Mantenha a etiqueta de postagem com o código de rastreamento visível.</li>
              <li>Entre em contato direto com o suporte do Cardumy e com a loja parceira pelo chat do pedido para acionamento do seguro de frete.</li>
            </ul>
          </div>
        )
      }
    ],
    colecoes: [
      {
        question: "Como funcionam as Pastas de Coleção (Binders)?",
        answer: "Na seção 'Pastas', você pode criar fichários temáticos com nomes personalizados (por exemplo, 'Coleção Pessoal', 'Pasta de Trocas' ou 'Cartas do Formato Atual'). É possível definir cada pasta como pública ou privada e gerar links compartilháveis para mostrar suas raridades para a comunidade."
      },
      {
        question: "Como o Deckbuilder valida as regras de cada jogo?",
        answer: "Nosso construtor de baralhos possui verificadores automáticos de regras específicas para cada TCG (como limites de 4 cópias, mínimo de 50 cartas para One Piece e Digimon, 60 para Pokémon, formato Commander de 100 cartas para Magic, regras de Leader, Eggs e restrições de Sideboard). O sistema sinaliza avisos de legalidade em tempo real enquanto você adiciona cartas."
      },
      {
        question: "Posso importar ou exportar minhas listas de decks?",
        answer: "Sim! Você pode importar listas a partir de texto copiado de simuladores e plataformas parceiras (como OPTCG Sim, Pokémon TCG Live, YGO Pro/Omega, MTG Arena) ou exportar a lista formatada com apenas um clique para inscrições em torneios oficiais."
      },
      {
        question: "Como funcionam as listas de desejos (Wantlist) e ofertas de troca?",
        answer: "Ao marcar cartas que você procura ou disponibilizar cópias excedentes para troca, a plataforma ajuda a identificar colecionadores e lojas próximas com interesses mútuos, facilitando negociações justas baseadas em cotações médias de mercado."
      }
    ],
    lojista: [
      {
        question: "Como credenciar minha loja física ou virtual como Lojista Parceiro?",
        answer: (
          <div className="space-y-2 text-slate-300">
            <p>
              Lojas especializadas contam com uma suíte completa de ferramentas no Cardumy:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Catálogo unificado sem necessidade de fotografar ou cadastrar dados de cartas individualmente.</li>
              <li>Controle de estoque, estados de conservação (Near Mint, Played, etc.) e acabamentos (Foil, Alternate Art).</li>
              <li>Organização de torneios com geração de emparceiramentos e relatórios de decklists.</li>
              <li>Checkout transparente com taxa competitiva de intermediação e proteção contra chargebacks.</li>
            </ul>
            <p className="pt-1 text-slate-300">
              Para iniciar o credenciamento, envie um e-mail para <strong className="text-purple-400">cardumyapp@gmail.com</strong> com o assunto "Credenciamento de Lojista".
            </p>
          </div>
        )
      },
      {
        question: "Como funciona a precificação e atualização de estoque no painel?",
        answer: "O painel do Lojista permite gerenciar quantidades, alterar preços individualmente ou em lote, definir promoções temporárias e sincronizar pedidos com cálculo automatizado de etiquetas de frete para envio rápido."
      },
      {
        question: "Como e quando ocorrem os repasses financeiros das vendas?",
        answer: "Os repasses são processados com total segurança e transparência após a confirmação de entrega do pedido, com relatórios detalhados de taxas, vendas brutas e líquidas acessíveis no painel administrativo da sua loja."
      }
    ],
    conta: [
      {
        question: "Como gerenciar meus endereços de entrega cadastrados?",
        answer: "Acesse o seu 'Perfil' e navegue até 'Gerenciar Endereços'. Você pode adicionar múltiplos CEPs (residencial, trabalho, etc.), definir um endereço padrão e atualizá-los a qualquer momento antes de fechar um pedido no carrinho."
      },
      {
        question: "Como alterar meu TCG favorito e preferências de catálogo?",
        answer: "Na tela de 'Editar Perfil', você pode escolher os jogos de seu maior interesse (como One Piece, Pokémon, Magic, Yu-Gi-Oh!). O Cardumy passará a priorizar banners, torneios locais e sugestões de cartas do seu universo favorito na página inicial e nas buscas."
      },
      {
        question: "Esqueci minha senha ou perdi o acesso, como proceder?",
        answer: "Na tela de Login, clique em 'Esqueci minha senha' e insira seu e-mail de cadastro para receber o link seguro de recuperação. Caso tenha criado sua conta usando o login social (Google), basta clicar novamente no botão correspondente."
      },
      {
        question: "Como entrar em contato direto com o suporte humano do Cardumy?",
        answer: (
          <div className="space-y-2 text-slate-300">
            <p>
              Nossa equipe de suporte está disponível para esclarecer dúvidas sobre pedidos, orientações para lojistas ou auxílio técnico:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">E-mail Oficial:</strong> <a href="mailto:cardumyapp@gmail.com" className="text-purple-400 hover:underline">cardumyapp@gmail.com</a></li>
              <li><strong className="text-white">Horário de Atendimento:</strong> Segunda a Sexta, das 09h às 18h (horário de Brasília).</li>
              <li><strong className="text-white">Tempo Médio de Resposta:</strong> Em até 24 horas úteis.</li>
            </ul>
          </div>
        )
      }
    ]
  };

  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-in fade-in duration-500">
      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-4xl font-black text-white italic uppercase tracking-tighter">Central de Ajuda</h1>
      </div>
      {/* Tabs Selector */}
      <div className="flex flex-wrap border-b border-slate-800 gap-2 pb-px justify-center md:justify-start">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setOpenIndex(null);
            }}
            className={`flex items-center space-x-2.5 px-6 py-4.5 border-b-2 font-black uppercase text-[10px] tracking-widest transition-all cursor-pointer ${
              activeTab === tab.id
                ? 'border-purple-500 text-white bg-purple-500/5'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            <i className={`fas ${tab.icon} text-xs`}></i>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Accordion List */}
      <div className="space-y-3.5">
        {faqs[activeTab].map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div 
              key={`faq-${activeTab}-${index}`}
              className="bg-slate-900/50 border border-slate-800 rounded-2.5xl overflow-hidden hover:border-slate-700/80 transition-all shadow-lg"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full text-left px-7 py-5.5 flex items-center justify-between text-white hover:text-purple-400 transition-colors cursor-pointer"
              >
                <span className="font-bold text-sm tracking-tight">{item.question}</span>
                <i className={`fas ${isOpen ? 'fa-minus' : 'fa-plus'} text-purple-400 text-xs shrink-0 ml-4`}></i>
              </button>
              {isOpen && (
                <div className="px-7 pb-6 text-slate-400 text-xs leading-relaxed border-t border-slate-800/40 pt-4 bg-slate-950/20">
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Direct Support Contact Card */}
      <div className="p-6 sm:p-8 rounded-2.5xl bg-gradient-to-r from-purple-950/20 via-[#090e1c] to-slate-900 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
        <div className="flex items-center gap-4 text-left">
          <div className="w-12 h-12 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0 shadow-inner">
            <i className="fas fa-headset text-lg"></i>
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-bold text-white">Não encontrou o que procurava?</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Nossa equipe de suporte está à disposição para te auxiliar com pedidos, integrações e sugestões.
            </p>
          </div>
        </div>

        <a
          href="mailto:cardumyapp@gmail.com?subject=Suporte%20Cardumy"
          className="shrink-0 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase text-[10px] tracking-widest rounded-xl transition-all shadow-lg hover:shadow-purple-600/20 flex items-center gap-2 cursor-pointer"
        >
          <i className="fas fa-envelope text-xs"></i>
          <span>Falar com o Suporte</span>
        </a>
      </div>

      {/* Version & Legal Footer */}
      <div className="text-center pt-2 pb-6 border-t border-slate-800/60 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
        <p className="text-xs text-slate-500 font-medium">
          Cardumy TCG • <span className="text-slate-400 font-mono font-bold">{versionControl.membro}</span>
        </p>
        <span className="hidden sm:inline text-slate-700">•</span>
        <Link to="/termos" className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors">
          Termos e Condições
        </Link>
      </div>
    </div>
  );
};
